<template>
  <!-- Eigener, einfarbiger Hintergrund statt des Startseiten-Bilds: QR-Code
       und Zugangsdaten muessen aus Sitzabstand lesbar sein. Einstellbar ueber
       castBgColor im Customizer des Portals. -->
  <div class="cast" :style="$store.getters['customizer/getCastStyles']">
    <div class="cast__header" :style="$store.getters['customizer/getCastHeaderStyles']">
      <div class="cast__back" :class="{ 'cast__back--active': backFocused }">
        <span class="cast__back-icon">&#8592;</span> {{ getBack }}
      </div>
      <div class="cast__title">{{ getTitle }}</div>
      <div class="cast__spacer"></div>
    </div>

    <!-- Auswahl der OTT-Apps. Kommt bewusst NACH der Verbindungsseite: das
         Netflix-Abkommen verlangt, dass zwischen Netflix-Kachel und Netflix
         nichts steht - die SoftAP-Seite liegt deshalb davor, nicht dazwischen. -->
    <div v-if="step === 'tiles'" class="cast__tiles">
      <!-- Waagerecht scrollend: sichtbar sind TILES_VISIBLE Kacheln, die Spur
           dahinter wird so verschoben, dass die fokussierte immer im Bild
           ist. Bewusst per transform und nicht per nativem Scrollen - das
           folgt auf den TV-Browsern der Fernbedienung nicht zuverlaessig. -->
      <div class="cast__grid">
        <div class="cast__arrow" :class="{ 'cast__arrow--hidden': !canScrollLeft }">&#8249;</div>
        <div class="cast__viewport" :style="viewportStyle">
          <div
            class="cast__track"
            :class="{ 'cast__track--centered': !scrollable }"
            :style="trackStyle"
          >
            <div
              v-for="(app, index) in apps"
              :key="app.key"
              class="cast__tile"
              :class="{ 'cast__tile--active': index === selectedIndex }"
              :style="tileStyle(app, index === selectedIndex)"
            >
              <!-- Laesst sich das Logo nicht laden (falsche URL, Server weg), wird
                   auf den Namen zurueckgefallen statt ein kaputtes Bild zu zeigen. -->
              <img
                v-if="app.logo && !logoFailed[app.key]"
                class="cast__tile-logo"
                :src="app.logo"
                :alt="app.name"
                @error="onLogoError(app)"
              />
              <div v-else class="cast__tile-name">{{ app.name }}</div>
            </div>
          </div>
        </div>
        <div class="cast__arrow" :class="{ 'cast__arrow--hidden': !canScrollRight }">&#8250;</div>
        <div v-if="apps.length === 0" class="cast__empty">{{ getNoApps }}</div>
      </div>
      <!-- Frueher stand das auf einer eigenen Seite hinter einer Cast-Kachel,
           die nichts startete. Jetzt direkt hier: jede Kachel startet ihre
           App, und wer lieber vom Handy castet, liest hier, wie. -->
      <div class="cast__footer">
        <div class="cast__footer-hint">{{ getCastTilesHint }}</div>
        <div class="cast__footer-hint" v-if="canLaunchCastEntry">{{ getCastEntryHint }}</div>
        <div class="cast__footer-hint" v-if="hasGuestData">{{ getClearHint }}</div>
        <!-- Der Weg zurueck zum QR-Code muss immer sichtbar sein: die Seite
             kann automatisch hierher gesprungen sein, obwohl das Handy des
             Gastes gar nicht (mehr) am WLAN des TV haengt. -->
        <div class="cast__footer-hint">{{ getTilesBackHint }}</div>
      </div>
    </div>

    <!-- Direktstart (eigene OTT-Kachel im Hauptmenue, z.B. Netflix): nach OK
         steht hier, was passiert - gestartet, nachinstalliert oder eben nicht
         verfuegbar. Frueher fiel die Seite bei einem Fehlschlag kommentarlos
         auf die Kachelliste zurueck (BU800 ohne Netflix: der Gast drueckte
         Netflix und bekam YouTube, Prime Video & Co. angeboten). -->
    <div v-else-if="step === 'launch'" class="cast__pairing">
      <div class="cast__pairing-row">
        <div class="cast__pairing-info">
          <div class="cast__pairing-app">{{ launchAppName }}</div>
          <template v-if="launchState === 'failed'">
            <div class="cast__pairing-text">{{ launchFailText }}</div>
            <div class="cast__pairing-text">{{ getUnavailableHint }}</div>
            <div class="cast__pairing-hint">{{ getUnavailableKeys }}</div>
            <!-- Fuer das Personal: der technische Grund, klein und blass. -->
            <div class="cast__launch-detail" v-if="launchError">{{ launchError }}</div>
          </template>
          <template v-else>
            <div class="cast__pairing-status">
              <span class="cast__pairing-dot"></span>{{ launchStatus }}
            </div>
            <div class="cast__pairing-hint" v-if="launchState === 'installing'">{{ getInstallWait }}</div>
          </template>
        </div>
      </div>
    </div>

    <!-- Verbindungsseite: erster Schritt fuer JEDEN Weg. Je nach Strategie
         eigener QR (Samsung SoftAP bzw. LG-WLAN) oder nur Hinweis, weil der TV
         das Pairing selbst anzeigt (LG >= 25). -->
    <div v-else class="cast__pairing">
      <div class="cast__pairing-row">
        <div class="cast__pairing-info">
          <div class="cast__pairing-app">{{ getConnectTitle }}</div>
          <div class="cast__pairing-text">{{ getPairingText }}</div>
          <!-- WLAN-Name und Passwort UNTEREINANDER. Nebeneinander mit
               Schraegstrich war auf dem Fernseher aus Sitzabstand kaum
               auseinanderzuhalten - und wer sie von Hand eintippt, sucht
               genau zwei Zeilen. -->
          <div class="cast__pairing-access" v-if="qrSvg && softAP.ssid">
            <div class="cast__pairing-ssid">{{ getWifiLabel }}: <strong>{{ softAP.ssid }}</strong></div>
            <div class="cast__pairing-ssid" v-if="softAP.key">
              {{ getPwLabel }}: <strong>{{ softAP.key }}</strong>
            </div>
          </div>
          <!-- Rueckmeldung, ob sich das Smartphone tatsaechlich mit dem SoftAP
               verbunden hat. Nur wenn der TV solche Ereignisse ueberhaupt
               meldet - sonst stuende hier ein "warte auf Verbindung", das nie
               umschlaegt. -->
          <div
            class="cast__pairing-status"
            v-if="pairing.watching"
            :class="{ 'cast__pairing-status--ok': pairing.connected }"
          >
            <span class="cast__pairing-dot"></span>{{ getPairingStatus }}
          </div>
          <!-- Weiter geht es automatisch, sobald sich ein Geraet verbindet.
               Meldet der TV keine Verbindungen (LG), bleibt nur die Taste. -->
          <!-- "Sobald verbunden: OK druecken" nur, wenn es ueberhaupt etwas zu
               verbinden gibt. Waehrend der Vorbereitung steht daneben noch
               "wird vorbereitet", und bei liegendem Hotspot kann sich gar
               nichts verbinden - in beiden Faellen war der Hinweis irrefuehrend
               (am BU800 mit abgeschaltetem Hotspot genau so passiert: die Seite
               versprach einen QR, der nie kam, und riet zugleich zu OK). -->
          <div class="cast__pairing-hint" v-if="autoForward > 0">{{ getStayHint }}</div>
          <div class="cast__pairing-hint" v-else-if="!preparing && !hotspotDown">{{ getContinueHint }}</div>
          <div class="cast__pairing-hint" v-if="canLaunchCastEntry">{{ getCastEntryHint }}</div>
          <!-- Die rote Taste steht nur da, wenn es ueberhaupt Anmeldedaten
               gibt - sonst bietet die Seite dem Gast an, etwas zu loeschen,
               das es nicht gibt. Gilt fuer beide Wege auf diese Seite, den
               Cast- wie den Netflix-Einstieg: es ist dieselbe Seite. -->
          <div class="cast__pairing-hint" v-if="hasGuestData">{{ getClearHint }}</div>
        </div>
        <div class="cast__qr" v-if="qrSvg" v-html="qrSvg"></div>
        <!-- Platzhalter, solange SSID und Passwort noch auf den TV geschrieben
             werden. Gleiche Groesse wie der QR, damit das Bild nicht springt -
             und der Gast sieht, WO der Code erscheinen wird. -->
        <div class="cast__qr cast__qr--wait" v-else-if="preparing || hotspotDown">
          <!-- Auch bei liegendem Hotspot: der pulsierende Punkt zeigt, dass die
               Seite es weiter versucht. Ein leerer Platz haette ausgesehen wie
               "hier kommt nichts mehr". -->
          <span class="cast__qr-dot"></span>
          <span class="cast__qr-tries" v-if="hotspotTries > 0">{{ hotspotTries }}</span>
        </div>
      </div>
    </div>

    <!-- Abmelden/Loeschen auf Wunsch des Gastes (rote Taste). Bewusst ein
         Overlay ueber JEDER Ansicht: der Gast soll es aus dem Weg raeumen
         koennen, ohne die Seite zu verlassen - und ohne dass ein Tastendruck
         daneben seine Anmeldung loescht. Vorbelegt ist deshalb "Abbrechen". -->
    <div class="cast__dialog" v-if="dialog">
      <div class="cast__dialog-box">
        <div class="cast__dialog-title">{{ getClearTitle }}</div>
        <div class="cast__dialog-text">{{ dialogText }}</div>
        <div class="cast__dialog-buttons" v-if="dialogButtons.length">
          <div
            class="cast__dialog-button"
            :class="{ 'cast__dialog-button--active': index === dialogIndex }"
            v-for="(button, index) in dialogButtons"
            :key="button.action"
          >
            {{ button.label }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import { NAV_TYPE } from '@/store/modules/navigation';
import QRCode from '@/helpers/QRCode';
import { CAST_APP_CATALOG } from '@/helpers/Cast';
import DeviceType from '@/helpers/DeviceType';
import TV from '@/helpers/TV';

// Sekunden, die die Verbindungsseite nach einer gemeldeten Verbindung stehen
// bleibt, bevor sie von selbst weitergeht. Lang genug, um "verbunden" zu lesen
// und notfalls mit ZURUECK beim QR-Code zu bleiben - kurz genug, dass der Gast
// nicht wartet.
const AUTO_FORWARD_S = 3;

// So lange muss die App mindestens im Hintergrund gewesen sein, damit die
// Rueckkehr als "aus einer anderen App zurueck" gilt und nach Home fuehrt.
// Kuerzere Ausfluege sind Firmware-Einblendungen ueber der App, kein App-Wechsel.
const FOREIGN_APP_MIN_MS = 1500;

// Kachelreihe: so viele Kacheln sind gleichzeitig sichtbar, und so breit ist
// eine Kachel samt Abstand (vw). Muss zu .cast__tile (18vw + 2 x 1vw Rand)
// passen - daraus wird die Verschiebung der Spur berechnet.
const TILES_VISIBLE = 4;
const TILE_PITCH_VW = 20;

// Abstand zwischen zwei Einschaltversuchen fuer den Hotspot.
//
// enableSoftAP ist synchron und haelt den Thread je Firmware zwischen 150 ms
// und rund zehn Sekunden. Ein enger Takt wuerde die Seite unbedienbar machen -
// deshalb bewusst gemuetlich. Der Gast liest in der Zeit ohnehin den Hinweis.
const HOTSPOT_RETRY_MS = 15000;

// Nach so vielen erfolglosen Versuchen wird aus "wird gestartet" der Hinweis
// an das Personal. Vorher waere er voreilig - der erste Versuch klappt oft.
const HOTSPOT_HINT_AFTER = 3;

// Der Geraete-Properties-Stand vom letzten Oeffnen der Cast-Seite.
//
// Bewusst auf Modulebene und nicht im Komponenten-State: die Seite wird bei
// jedem Betreten neu aufgebaut, der Vergleich soll aber ueber mehrere Besuche
// hinweg funktionieren. Haelt nur bis zum naechsten App-Start - laenger waere
// er fuer die Frage "was hat sich zwischen zwei Verbindungsversuchen
// geaendert" ohnehin nicht aussagekraeftig.
let lastProps = null;

export default {
  mixins: [keyHandler],
  data() {
    return {
      // 'pairing' -> Verbindungsseite (immer zuerst, auch fuer Netflix)
      // 'tiles'   -> Kachelauswahl (mit Cast-Hinweis)
      // 'launch'  -> Direktstart laeuft bzw. ist gescheitert (launchState)
      step: 'pairing',
      // Direktstart: 'launching' | 'installing' | 'failed' ('' = keiner).
      launchState: '',
      // Beim Direktstart wurde eine Nachinstallation versucht - dann lautet
      // der Fehlschlag "konnte nicht installiert werden".
      installTried: false,
      // Fortschritt der Nachinstallation in Prozent (null = unbekannt).
      installProgress: null,
      // Technischer Grund eines gescheiterten Direktstarts (fuer das Personal).
      launchError: '',
      qrSvg: '',
      backFocused: false,
      // Eine Fremd-App (OTT bzw. nativer Cast-Einstieg) wurde von hier
      // gestartet - beim Zurueckkommen wird nach Home navigiert.
      externalAppLaunched: false,
      // Kacheln, deren Logo nicht geladen werden konnte -> Textfallback.
      logoFailed: {},
      // Die Verbindungsseite ist noch nicht fertig: erst laeuft die
      // Geraete-Erkennung (cast/init), dann werden SSID und Passwort auf den TV
      // geschrieben (preparePairing). Zusammen dauert das ein paar Sekunden, und
      // solange darf hier weder ein QR noch die Anleitung fuer den nativen Weg
      // stehen. Startwert deshalb true - die Seite oeffnet immer im Wartezustand.
      preparing: true,
      // Der Hotspot des TV sendet nicht. Dann wird KEIN QR gezeigt - er waere
      // ein Code, der ins Nichts fuehrt - sondern ein Hinweis.
      hotspotDown: false,
      // Laufende Einschaltversuche, solange die Seite offen ist. Erst wenn
      // mehrere gescheitert sind, wird aus "wird gestartet" ein "wenden Sie
      // sich an das Personal" - vorher waere das voreilig.
      hotspotTries: 0,
      hotspotTimer: null,
      // Offene Sicherheitsabfrage bzw. Rueckmeldung zum Loeschen der
      // Gastdaten. '' = kein Dialog. Zustaende:
      //   'confirm' -> "wirklich loeschen?"
      //   'busy'    -> laeuft (Firmware-Aufruf, keine Tasten)
      //   'done'    -> geloescht
      //   'reboot'  -> geloescht wird erst mit einem Neustart: jetzt oder spaeter
      //   'later'   -> der Gast hat "spaeter" gewaehlt
      dialog: '',
      dialogIndex: 0,
      // Katalog-Schluessel der App, die diese Seite starten soll, sobald die
      // Verbindung steht ('netflix'). Kommt aus der Adresse (/cast?app=netflix)
      // und damit von der eigenen OTT-Kachel im Hauptmenue.
      //
      // Der Weg fuehrt bewusst NICHT direkt in die App: auch fuer Netflix
      // braucht der Gast zuerst das WLAN des Fernsehers - ohne Netz am
      // Smartphone gibt es keine Anmeldung per Code. Statt einer Kachelauswahl
      // steht danach nur noch der Start dieser einen App.
      directKey: '',
      // Countdown bis zum automatischen Weitergehen (Sekunden, 0 = keiner).
      autoForward: 0,
      autoTimer: null,
      // Der Gast hat den Countdown mit ZURUECK abgebrochen - dann springt die
      // Seite nicht erneut nur deshalb, weil das Handy schon als verbunden gilt.
      // Eine NEUE Verbindung (Watcher) leitet trotzdem weiter.
      autoForwardDeclined: false,
      // Zeitpunkt, zu dem die App in den Hintergrund ging (0 = sichtbar).
      hiddenAt: 0,
      // Index der ersten sichtbaren Kachel in der scrollenden Reihe.
      firstTile: 0,
    };
  },
  computed: {
    apps() {
      return this.$store.getters['cast/getApps'];
    },
    selectedIndex() {
      return this.$store.state.cast.selectedIndex;
    },
    selectedApp() {
      return this.$store.getters['cast/getSelectedApp'];
    },
    // Scrollen nur, wenn mehr Kacheln da sind als in die Reihe passen. Sonst
    // bleibt die Reihe zentriert wie bisher.
    scrollable() {
      return this.apps.length > TILES_VISIBLE;
    },
    canScrollLeft() {
      return this.scrollable && this.firstTile > 0;
    },
    canScrollRight() {
      return this.scrollable && this.firstTile + TILES_VISIBLE < this.apps.length;
    },
    // Das Sichtfenster ist genau TILES_VISIBLE Kacheln breit; ohne Scrollen
    // passt es sich dem Inhalt an.
    viewportStyle() {
      return this.scrollable ? { width: TILES_VISIBLE * TILE_PITCH_VW + 'vw' } : {};
    },
    trackStyle() {
      if (!this.scrollable) return {};
      const x = -this.firstTile * TILE_PITCH_VW;
      return { transform: 'translateX(' + x + 'vw)', webkitTransform: 'translateX(' + x + 'vw)' };
    },
    softAP() {
      return this.$store.state.cast.softAP;
    },
    pairing() {
      return this.$store.state.cast.pairing;
    },
    strategy() {
      return this.$store.getters['cast/getStrategy'];
    },
    // Nativer Cast-Einstieg (Bildschirmuebertragung) ist nur auf LG sinnvoll.
    //
    // Haengt bewusst an den Geraete-Faehigkeiten und nicht mehr an der
    // Strategie: seit der SoftAP-QR auf LG Vorrang hat, waere der Einstieg
    // sonst genau auf den Geraeten verschwunden, die ihn anbieten.
    //
    // Samsung (seit 06.10.2026): die blaue Taste oeffnet die Google-Cast-
    // Einstellungen (googlecast.launchSettings, Samsungs Hotel-SI API-TC 5 fuer
    // das Guest-paired Network). Am BU800 war der TV trotz Google Cast READY
    // fuer das Handy im SoftAP nicht sichtbar; laut Vorlage gehoert genau
    // dieser Schritt zum SoftAP-Pairing.
    canLaunchCastEntry() {
      const caps = this.$store.state.cast.capabilities;
      if (caps && caps.detected) {
        if (caps.hasGoogleCast) return true;
        return !!(caps.castEntryId || (caps.screenShareIds && caps.screenShareIds.length));
      }
      return this.strategy === 'lg-native-cast' || this.strategy === 'screenshare';
    },
    // Der Katalogeintrag zur direkt zu startenden App (fuer Titel und Start).
    // Zuerst die fuer dieses Geraet konfigurierte Liste, dann der Katalog -
    // dieselbe Reihenfolge wie in cast/launchByKey.
    directApp() {
      if (!this.directKey) return null;
      const configured = this.apps.find((a) => a.key === this.directKey);
      if (configured) return configured;
      return CAST_APP_CATALOG.find((a) => a.key === this.directKey) || null;
    },
    getTitle() {
      // Kommt der Gast ueber die Netflix-Kachel, steht auch Netflix im Kopf -
      // "Cast" waere dort das falsche Versprechen.
      if (this.directApp) return this.directApp.name;
      return this.$i18n('cast.title');
    },
    getBack() {
      return this.$i18n('back.name');
    },
    // Pairing-Text passend zur Strategie:
    //  - eigener QR (SoftAP, Samsung + LG)    -> cast.pairing
    //  - QR auf das WLAN des TV (LG < 25)     -> cast.pairingNetwork
    //  - TV zeigt Pairing selbst (LG >= 25)   -> cast.pairingNative
    //  - nur Screen Share                     -> cast.pairingScreenShare
    getPairingText() {
      // Zuerst der Wartehinweis. Ohne ihn haengt der Text an "noch kein QR da"
      // und faellt auf pairingNative zurueck - der Gast liest also ein paar
      // Sekunden lang die Anleitung fuer den nativen Weg, bis der QR erscheint
      // und der Text umspringt.
      if (this.preparing) return this.$i18n('cast.preparing');
      // Vor allem anderen: laeuft der Hotspot ueberhaupt? Ohne ihn ist jede
      // Anleitung falsch, egal welche Strategie.
      if (this.hotspotDown) {
        // Solange noch Versuche laufen: sagen, dass etwas passiert. Der
        // Personal-Hinweis kommt erst, wenn es mehrfach nicht geklappt hat.
        return this.hotspotTries < HOTSPOT_HINT_AFTER
          ? this.$i18n('cast.hotspotStarting')
          : this.$i18n('cast.hotspotDown');
      }
      if (this.strategy === 'network-qr') {
        return this.qrSvg ? this.$i18n('cast.pairingNetwork') : this.$i18n('cast.pairingNative');
      }
      if (this.strategy === 'lg-native-cast') return this.$i18n('cast.pairingNative');
      if (this.strategy === 'screenshare') return this.$i18n('cast.pairingScreenShare');
      return this.qrSvg ? this.$i18n('cast.pairing') : this.$i18n('cast.pairingNative');
    },
    getCastEntryHint() {
      const caps = this.$store.state.cast.capabilities;
      if (caps && caps.hasGoogleCast) return this.$i18n('cast.castSettingsHint');
      return this.$i18n('cast.castEntryHint');
    },
    getConnectTitle() {
      return this.$i18n('cast.connectTitle');
    },
    launchAppName() {
      return (this.directApp && this.directApp.name) || this.directKey;
    },
    launchStatus() {
      if (this.launchState !== 'installing') return this.launchText('launching');
      const pct = this.installProgress === null ? '' : ' ' + this.installProgress + ' %';
      return this.launchText('installing') + pct;
    },
    launchFailText() {
      return this.launchText(this.installTried ? 'installFailed' : 'unavailable');
    },
    getInstallWait() {
      return this.$i18n('cast.installWait');
    },
    getUnavailableHint() {
      return this.$i18n('cast.unavailableHint');
    },
    getUnavailableKeys() {
      return this.$i18n('cast.unavailableKeys');
    },
    getContinueHint() {
      return this.$i18n('cast.continueHint');
    },
    getCastTilesHint() {
      return this.$i18n('cast.castTilesHint');
    },
    // Meldet der TV den Geraetenamen mit (Tizen liefert ClientName), dann den
    // zeigen - "OnePlus-13 verbunden" ist fuer den Gast die eindeutige
    // Bestaetigung, dass SEIN Handy angekommen ist. Bei mehreren Geraeten die
    // Anzahl dazu, sonst wirkt die Meldung wie "genau dein Handy", obwohl
    // schon jemand anders dranhaengt.
    getPairingStatus() {
      if (!this.pairing.connected) return this.$i18n('cast.waitingForPhone');
      let text = this.$i18n('cast.phoneConnected');
      if (this.pairing.name) text = this.pairing.name + ' - ' + text;
      if (this.pairing.clients > 1) text += ' (' + this.pairing.clients + ')';
      if (this.autoForward > 0) text += ' - ' + this.$i18n('cast.autoContinue') + ' ' + this.autoForward + ' s';
      return text;
    },
    getStayHint() {
      return this.$i18n('cast.stayHint');
    },
    getTilesBackHint() {
      return this.$i18n('cast.tilesBackHint');
    },
    getNoApps() {
      return this.$i18n('cast.noApps');
    },
    // Liegen ueberhaupt Anmeldedaten des Gastes auf dem Fernseher?
    //
    // Die Entscheidung liegt im Store (cast/hasGuestData) - dort steht auch,
    // warum auf LG der eingerichtete SoftAP als Indiz zaehlt: Clients lassen
    // sich dort nicht messen, und ein Cast-Start vom Handy hinterliess deshalb
    // keine Spur. Die Merker selbst liegen im localStorage und ueberleben einen
    // Reload der App.
    hasGuestData() {
      return this.$store.getters['cast/hasGuestData'];
    },
    getClearHint() {
      return this.$i18n('cast.clearHint');
    },
    getClearTitle() {
      return this.$i18n('cast.clearTitle');
    },
    dialogText() {
      switch (this.dialog) {
        case 'confirm':
          return this.$i18n('cast.clearText');
        case 'busy':
          return this.$i18n('cast.clearBusy');
        case 'done':
          return this.$i18n('cast.clearDone');
        case 'reboot':
          return this.$i18n('cast.clearRebootText');
        case 'later':
          return this.$i18n('cast.clearLater');
        default:
          return '';
      }
    },
    // Die harmlose Antwort steht IMMER an erster Stelle und ist damit
    // vorbelegt (dialogIndex 0): ein Gast, der versehentlich ROT und dann OK
    // drueckt, loescht nichts.
    dialogButtons() {
      switch (this.dialog) {
        case 'confirm':
          return [
            { action: 'cancel', label: this.$i18n('cast.clearCancel') },
            { action: 'clear', label: this.$i18n('cast.clearConfirm') },
          ];
        case 'reboot':
          return [
            { action: 'cancel', label: this.$i18n('cast.clearRebootLater') },
            { action: 'reboot', label: this.$i18n('cast.clearRebootNow') },
          ];
        case 'done':
        case 'later':
          return [{ action: 'cancel', label: this.$i18n('cast.clearClose') }];
        default:
          // 'busy': keine Auswahl - der Firmware-Aufruf laeuft.
          return [];
      }
    },
    getWifiLabel() {
      return this.$i18n('cast.wifi');
    },
    getPwLabel() {
      return this.$i18n('cast.password');
    },
  },
  methods: {
    // Schiebt die Reihe so weit, dass die fokussierte Kachel sichtbar ist -
    // um so wenig wie moeglich, damit die Reihe beim Durchblaettern nicht
    // springt. Beim Umlauf (letzte -> erste) geht es entsprechend zurueck.
    keepSelectedVisible() {
      const count = this.apps.length;
      if (count <= TILES_VISIBLE) {
        this.firstTile = 0;
        return;
      }
      const sel = Math.max(0, Math.min(this.selectedIndex, count - 1));
      let first = this.firstTile;
      if (sel < first) first = sel;
      if (sel > first + TILES_VISIBLE - 1) first = sel - TILES_VISIBLE + 1;
      this.firstTile = Math.max(0, Math.min(first, count - TILES_VISIBLE));
    },
    onLogoError(app) {
      this.$set(this.logoFailed, app.key, true);
    },
    tileStyle(app) {
      // Kachel weiss mit Logo; die OTT-Markenfarbe nur noch als Rahmen.
      // Der Fokus-Zustand (dickerer Rahmen/Glow/Scale) kommt aus der
      // .cast__tile--active-Klasse und ueberschreibt die Rahmenfarbe nicht.
      return {
        backgroundColor: '#ffffff',
        borderColor: app.color || '#cccccc',
      };
    },
    // Verbindungsseite vorbereiten. Laeuft beim Betreten der Seite, VOR jeder
    // App-Auswahl - das Handy soll in jedem Fall zuerst am TV haengen.
    //
    // Je nach Strategie:
    //  - softap-qr (Samsung, LG als Rueckfallebene): eigenen SoftAP
    //    konfigurieren -> WLAN-QR.
    //  - network-qr (LG < 25): SSID/Key des verbundenen WLANs -> WLAN-QR,
    //    damit das Handy im selben Netz landet wie der TV.
    //  - lg-native-cast (LG >= 25): kein eigener QR; der TV zeigt sein
    //    Cast-/AirPlay-Pairing selbst an.
    //  - screenshare: kein QR, nur Hinweis.
    async preparePairing() {
      this.qrSvg = '';
      // Ab hier laeuft die Vorbereitung: auf Samsung werden SSID und Passwort
      // geschrieben und der SoftAP neu gestartet (synchron, mehrere Sekunden),
      // auf LG sind es bis zu sechs hcap-Aufrufe der Reihe nach. Erst danach
      // darf der QR entstehen - er MUSS zur echten Broadcast-Konfiguration
      // passen, sonst kommt das Handy nicht ins Netz.
      this.preparing = true;
      this.hotspotDown = false;
      let pairing = { qr: false };
      // Einen Frame abwarten, damit "Verbindung wird vorbereitet" WIRKLICH auf
      // dem Schirm steht, bevor die Geraete-Aufrufe losgehen. Die SoftAP-Aufrufe
      // sind synchron und blockieren den Thread mehrere Sekunden - ohne diese
      // Pause bliebe der alte Bildinhalt stehen und die Seite waere still.
      await new Promise((resolve) => setTimeout(resolve, 60));
      try {
        pairing = await this.$store.dispatch('cast/configurePairing');
      } catch (e) {
        console.log('[Cast] configurePairing failed', e);
      }
      this.hotspotDown = !!(pairing && pairing.hotspotDown);
      if (this.hotspotDown) this.startHotspotRetry();
      else this.stopHotspotRetry();
      if (pairing && pairing.qr) {
        const payload = this.$store.getters['cast/getWifiQrPayload'];
        try {
          this.qrSvg = QRCode.toSVG(payload, {
            ecc: 'M',
            moduleSize: 8,
            margin: 4,
            dark: '#000000',
            light: '#ffffff',
          });
        } catch (e) {
          console.log('[Cast] QR generation failed', e);
          this.qrSvg = '';
        }
      }
      // Egal wie es ausging: der Wartehinweis muss weg. Bleibt er stehen,
      // sieht der Gast auf einem Geraet ohne QR fuer immer "wird vorbereitet".
      this.preparing = false;
      this.logPairingDecision(pairing);
      // Ab jetzt melden, ob das Smartphone am SoftAP ankommt. Der Watcher auf
      // pairing.connected schaltet dann weiter zu den Kacheln. Bei liegendem
      // Hotspot entfaellt das - dort kann sich nichts verbinden.
      if (!this.hotspotDown) this.$store.dispatch('cast/watchSoftAP');
      // Haengt das Handy schon (der Listener laeuft ueber die Seite hinaus
      // weiter, siehe cast/watchSoftAP), kommt kein neues Ereignis - der
      // Watcher auf pairing.connected wuerde also nie ausloesen. Dann hier
      // weiterleiten, mit demselben Countdown.
      if (this.pairing.connected && !this.autoForwardDeclined) this.startAutoForward();
    },
    // Geht nach AUTO_FORWARD_S Sekunden von selbst weiter - zur Kachelauswahl
    // bzw. bei einem Direktstart (Netflix) sofort in die App.
    //
    // Bewusst mit Countdown statt sofort: "verbunden" kann veraltet sein (ein
    // verpasstes Trennen-Ereignis), und der Gast muss im Zweifel beim QR-Code
    // bleiben koennen. ZURUECK bricht ab, OK geht sofort weiter.
    startAutoForward() {
      if (this.step !== 'pairing' || this.preparing || this.hotspotDown || this.autoTimer) return;
      this.autoForward = AUTO_FORWARD_S;
      this.autoTimer = setInterval(() => {
        this.autoForward -= 1;
        if (this.autoForward > 0) return;
        this.cancelAutoForward();
        if (this.step === 'pairing') this.showTiles();
      }, 1000);
    },
    cancelAutoForward() {
      if (this.autoTimer) clearInterval(this.autoTimer);
      this.autoTimer = null;
      this.autoForward = 0;
    },
    // Warum steht auf dieser Seite ein QR - oder eben keiner? In EINER Zeile.
    //
    // Der Anlass: auf einem UK660H/UM767H kam kein QR und keine SSID, und aus
    // der Ferne war nicht zu entscheiden, welcher der drei moeglichen Gruende
    // vorlag (Strategie 'lg-native-cast', ausgefallene Faehigkeitserkennung
    // oder liegender Hotspot) - sie sehen auf dem Schirm identisch aus.
    //
    // Warum hier und nicht in detect(): dessen Zeile "Strategie-Basis" wird
    // wegen der gecachten _detectPromise nur EINMAL pro App-Start geschrieben,
    // also lange bevor jemand das Live-Log oeffnet. Diese hier faellt bei jedem
    // Oeffnen der Cast-Seite an, und damit genau dann, wenn jemand zusieht.
    //
    // tv_name steht mit ROHWERT UND Schreibbarkeit drin, und das ist kein
    // Zufall: auf einem 43UK660H ist die Property schreib-, aber nicht lesbar
    // (getProperty null, setProperty result:true). Genau daran hing, dass die
    // Seite dort auf 'screenshare' fiel - siehe CastLG._refreshSoftApQr().
    // Ein tv_name mit Wert bei tvNameSchreibbar=true kann deshalb auch einfach
    // der Wert sein, den wir selbst geschrieben haben.
    logPairingDecision(pairing) {
      try {
        const cast = this.$store.state.cast;
        const caps = cast.capabilities || {};
        const props = caps.properties || {};
        const raw = (v) => (v === null ? 'null' : v === undefined ? '-' : '"' + v + '"');
        console.log(
          '[Cast] Verbindungsseite: strategie=' + this.strategy +
          ' qr=' + !!(pairing && pairing.qr) +
          ' ssid=' + raw(this.softAP && this.softAP.ssid) +
          ' hotspotDown=' + this.hotspotDown +
          ' | detected=' + !!caps.detected +
          ' softApQr=' + !!caps.softApQr +
          ' castEntry=' + (caps.castEntryId || '-') +
          ' googleCast=' + !!caps.googleCast +
          ' networkQr=' + !!caps.networkQr +
          ' tv_name=' + raw(props.tv_name) +
          ' tvNameSchreibbar=' + (caps.tvNameWritable === null ? '?' : !!caps.tvNameWritable) +
          ' soft_ap=' + raw(props.soft_ap) +
          // Ohne DIAL findet das Handy den TV nicht, auch wenn der Hotspot
          // laeuft und die WLAN-Verbindung steht - genau so am Geraet nach
          // einem Neustart aufgetreten.
          ' dial=' + raw(props.dial) +
          ' screenShare=' + raw(props.wifi_screen_share) +
          ' asl_mode=' + raw(props.asl_mode) +
          ' webos=' + (caps.webos || '-') +
          ' versionUnsicher=' + DeviceType.isWebOSVersionUnreliable +
          // Der Sitzungszustand gehoert dazu, seit die Cast-Nutzung beim
          // ZWEITEN Mal scheiterte (Handy am SoftAP, aber kein Geraet in der
          // Cast-Liste). Beim ersten Oeffnen steht hier alles auf false; kommt
          // der Gast aus einer OTT-App zurueck, zeigt die Zeile, was das
          // Aufraeumen inzwischen angefasst hat - und ob der Hotspot dabei neu
          // aufgesetzt (soft_ap 0->1, wirft jedes verbundene Handy raus) oder
          // nur eingeschaltet wurde.
          ' | sitzung=' + !!cast.sessionActive +
          ' aufraeumenOffen=' + !!cast.clearPending +
          ' softApAktiv=' + !!cast.softApActive +
          ' verbunden=' + !!(cast.pairing && cast.pairing.connected) +
          ' clients=' + ((cast.pairing && cast.pairing.clients) || 0)
        );
        // ALLE Cast-Properties als Rohwerte, in einer zweiten Zeile.
        //
        // Der Anlass: nach einem Power Off/On fand das Handy den TV nicht mehr,
        // obwohl der Hotspot lief, die WLAN-Verbindung stand UND dial="1"
        // gemeldet wurde. Damit scheiden die drei Flags aus, die wir selbst
        // setzen - es kippt etwas anderes.
        //
        // Welche Property das ist, laesst sich nur durch VERGLEICH finden:
        // einmal diese Zeile im funktionierenden Zustand, einmal nach dem
        // Neustart. Deshalb stehen hier alle, die das Geraet kennt, und nicht
        // eine ausgewaehlte Handvoll - die Auswahl waere schon wieder geraten.
        const keys = Object.keys(props).sort();
        console.log(
          '[Cast] Geraete-Properties: ' +
          (keys.length
            ? keys.map((k) => k + '=' + raw(props[k])).join(' ')
            : '(keine gelesen)')
        );
        // Und was sich seit dem letzten Oeffnen GEAENDERT hat.
        //
        // Der Vergleich ist bei dieser Suche das Entscheidende: "YouTube findet
        // den TV beim zweiten Mal nicht mehr" heisst, dass zwischen dem ersten
        // und dem zweiten Oeffnen etwas gekippt ist. Bisher musste dafuer jemand
        // zwei Logs sammeln und nebeneinanderlegen - im Moment des Fehlers hat
        // aber niemand mehr das "Vorher". Deshalb merkt sich die Seite den
        // letzten Stand selbst und nennt nur noch den Unterschied.
        //
        // Beim ersten Oeffnen gibt es nichts zu vergleichen; dann bleibt es bei
        // der Zeile darueber.
        if (lastProps) {
          const diff = Object.keys(Object.assign({}, lastProps, props))
            .sort()
            .filter((k) => raw(lastProps[k]) !== raw(props[k]))
            .map((k) => k + ': ' + raw(lastProps[k]) + ' -> ' + raw(props[k]));
          console.log(
            '[Cast] Properties seit dem letzten Oeffnen: ' +
            (diff.length ? diff.join(' | ') : 'unveraendert')
          );
        }
        lastProps = Object.assign({}, props);
      } catch (e) {
        /* Diagnose darf die Seite nicht anhalten */
      }
    },
    // Versucht wiederholt, den Hotspot anzuschalten, solange diese Seite offen
    // ist und er nicht sendet.
    //
    // Der Anlass: auf einem U800F stand der Hotspot aus, die Seite sagte es
    // korrekt - und danach passierte nichts mehr. Solange im Hauptmenue eine
    // Cast-App steht, soll der Hotspot aber laufen, und die Cast-Seite ist der
    // Ort, an dem eine Wartezeit sichtbar sein darf.
    //
    // Ein Versuch pro Runde, mit Abstand: enableSoftAP ist synchron.
    startHotspotRetry() {
      this.stopHotspotRetry();
      if (!this.hotspotDown) return;
      this.hotspotTimer = setInterval(() => {
        if (!this.hotspotDown) {
          this.stopHotspotRetry();
          return;
        }
        this.hotspotTries += 1;
        console.log('[Cast] Hotspot-Einschaltversuch ' + this.hotspotTries);
        this.$store
          .dispatch('cast/ensureHotspot')
          .then((up) => {
            if (up !== true) return;
            console.log('[Cast] Hotspot laeuft - Verbindungsseite neu aufbauen');
            this.stopHotspotRetry();
            // Jetzt kann alles entstehen, was ohne Hotspot sinnlos war:
            // Zugangsdaten, QR, Verbindungsbeobachtung.
            this.preparePairing();
          })
          .catch(() => {});
      }, HOTSPOT_RETRY_MS);
    },
    stopHotspotRetry() {
      if (!this.hotspotTimer) return;
      clearInterval(this.hotspotTimer);
      this.hotspotTimer = null;
    },
    // Weiter von der Verbindungsseite: normalerweise zur Kachelauswahl - bei
    // einem Direktstart (Netflix-Kachel im Hauptmenue) sofort in die App.
    showTiles() {
      this.cancelAutoForward();
      this.backFocused = false;
      if (this.directKey) {
        this.launchDirect();
        return;
      }
      this.step = 'tiles';
    },
    // Startet die App, mit der diese Seite geoeffnet wurde.
    //
    // Waehrenddessen steht die Startansicht ('launch'): "wird gestartet", bei
    // einer Nachinstallation "wird installiert … x %". Scheitert der Start,
    // bleibt der Gast dort und liest, dass die App gerade nicht geht - OK
    // fuehrt zu den uebrigen Apps, ZURUECK auf die Startseite. Die rote
    // Fehlermeldung des Stores ist dabei abgeschaltet (quiet): sie zeigte dem
    // Gast Texte wie ERROR_NO_TOKEN_APP_DATA.
    launchDirect() {
      const key = this.directKey;
      if (!key) return;
      // Nur EIN Start: OK zweimal gedrueckt, oder das Smartphone verbindet sich
      // erneut, waehrend die App schon im Vordergrund laeuft - beides darf sie
      // nicht ein zweites Mal starten. Der Merker faellt erst, wenn wir aus dem
      // Hintergrund zurueckkommen (onVisibilityChange).
      if (this.externalAppLaunched) return;
      this.externalAppLaunched = true;
      this.step = 'launch';
      this.launchState = 'launching';
      this.installTried = false;
      this.installProgress = null;
      this.launchError = '';
      this.$store
        .dispatch('cast/launchByKey', { key: key, quiet: true, onProgress: (p) => this.onInstallProgress(p) })
        .catch((err) => {
          this.externalAppLaunched = false;
          this.launchState = 'failed';
          this.launchError = String((err && (err.message || err.name)) || err || '');
          this.silenceTuner();
        });
    },
    onInstallProgress(progress) {
      this.installTried = true;
      if (this.launchState === 'failed') return;
      this.launchState = 'installing';
      const n = Math.round(Number(progress && progress.progress));
      if (!isNaN(n)) this.installProgress = Math.max(0, Math.min(100, n));
    },
    // OK nach gescheitertem Direktstart: weiter zu den uebrigen Apps.
    leaveLaunch() {
      this.directKey = '';
      this.launchState = '';
      this.step = 'tiles';
    },
    launchText(key) {
      return String(this.$i18n('cast.' + key) || '').replace('{app}', this.launchAppName);
    },
    // Auswahl einer Kachel: jede Kachel (YouTube, Prime Video, Spotify, ...)
    // startet SOFORT ihre App, ohne Zwischenseite - bei Netflix ist das
    // vertragliche Auflage, siehe PAIRING_MODE in helpers/Cast. Wer vom Handy
    // castet, braucht keine Kachel; der Hinweis dazu steht auf der Kachelseite.
    chooseSelected() {
      if (!this.selectedApp) return;
      this.launchApp();
    },
    // Startet die gewaehlte OTT-App und merkt sich, dass jetzt eine FREMDE App
    // im Vordergrund steht (siehe onVisibilityChange).
    //
    // Scheitert der Start, faellt der TV auf seinen eigenen Tuner zurueck und
    // dessen Ton laeuft im Hintergrund weiter - deshalb im Fehlerfall stumm
    // schalten.
    launchApp() {
      this.externalAppLaunched = true;
      this.$store.dispatch('cast/launchSelected').catch(() => {
        this.externalAppLaunched = false;
        this.silenceTuner();
      });
    },
    // Oeffnet den nativen Cast-/Screen-Share-Einstieg des TV. Bewusst eine
    // eigene Aktion (blaue Taste), damit sie nicht mit dem Start der OTT-App
    // kollidiert - beides sind unterschiedliche App-Starts.
    openCastEntry() {
      if (!this.canLaunchCastEntry) return;
      this.externalAppLaunched = true;
      this.$store.dispatch('cast/launchCastEntry').catch(() => {
        this.externalAppLaunched = false;
      });
    },
    // Rote Taste: der Gast will seine Anmeldedaten selbst entfernen.
    //
    // Ohne Daten passiert nichts - dann steht auch kein Hinweis auf dem
    // Schirm, und eine Abfrage "wirklich loeschen?" ueber einem leeren
    // Fernseher waere nur verwirrend.
    openClearDialog() {
      if (!this.hasGuestData) return;
      this.backFocused = false;
      this.dialogIndex = 0;
      this.dialog = 'confirm';
    },
    closeDialog() {
      this.dialog = '';
      this.dialogIndex = 0;
    },
    // Tasten, solange ein Dialog offen ist. Er faengt ALLES ab - sonst wuerde
    // der Gast hinter der Abfrage weiternavigieren oder eine App starten.
    dialogKey(key) {
      const buttons = this.dialogButtons;
      switch (key) {
        case KEYS.LEFT:
          if (buttons.length) this.dialogIndex = this.dialogIndex > 0 ? this.dialogIndex - 1 : buttons.length - 1;
          return;
        case KEYS.RIGHT:
          if (buttons.length) this.dialogIndex = this.dialogIndex + 1 < buttons.length ? this.dialogIndex + 1 : 0;
          return;
        case KEYS.BACK:
        case KEYS.GOBACK:
        case KEYS.EXIT:
          // ZURUECK ist immer die harmlose Antwort: Abfrage weg, nichts
          // geloescht bzw. kein Neustart.
          if (this.dialog !== 'busy') this.closeDialog();
          return;
        case KEYS.ENTER:
          if (!buttons.length) return;
          this.dialogActivate(buttons[this.dialogIndex].action);
          return;
        default:
          return;
      }
    },
    dialogActivate(action) {
      if (action === 'clear') {
        this.runClear();
        return;
      }
      if (action === 'reboot') {
        this.runReboot();
        return;
      }
      // 'cancel' - und aus 'reboot' heraus heisst das "spaeter beim
      // Ausschalten". Das Loeschen ist dann scharf geschaltet, der Gast soll
      // aber wissen, dass es noch nicht passiert ist.
      if (this.dialog === 'reboot') {
        this.dialogIndex = 0;
        this.dialog = 'later';
        return;
      }
      this.closeDialog();
    },
    runClear() {
      this.dialogIndex = 0;
      this.dialog = 'busy';
      this.$store
        .dispatch('cast/clearGuestDataNow')
        .then((result) => {
          this.dialogIndex = 0;
          // needsReboot heisst: ohne Neustart ist das Loeschen nicht fertig -
          // entweder weil die Firmware gar kein Sofort-Loeschen kennt (dann ist
          // es nur vorgemerkt), oder weil der Checkout zwar lief, die laufende
          // Firmware den geraeumten Stand aber erst nach dem Neustart zeigt
          // (LG, am 43UK660H beobachtet). In beiden Faellen bekommt der Gast
          // den Neustart angeboten statt ein "erledigt", das er gleich
          // widerlegt sieht.
          const needsReboot = !result || !result.deleted || result.needsReboot;
          this.dialog = needsReboot ? 'reboot' : 'done';
        })
        .catch(() => {
          this.dialogIndex = 0;
          this.dialog = 'reboot';
        });
    },
    runReboot() {
      this.dialogIndex = 0;
      this.dialog = 'busy';
      this.$store.dispatch('cast/rebootForClear').catch(() => {
        this.dialogIndex = 0;
        this.dialog = 'later';
      });
    },
    // Stoppt Tuner und Media-Playback. Die Cast-Seite zeigt kein Video; laeuft
    // der Tuner weiter, hoert der Gast dessen Ton im Hintergrund - entweder den
    // zuletzt gespielten Kanal oder einen aus der geraeteeigenen Senderliste,
    // auf die der TV nach einem fehlgeschlagenen App-Start zurueckfaellt.
    silenceTuner() {
      TV.stopPlayback('Cast-Seite');
    },
    // Kommt die App aus dem Hintergrund zurueck (der Gast verlaesst die
    // gestartete OTT-App), laeuft mounted() nicht erneut - der Tuner-Ton waere
    // dann wieder zu hoeren. Deshalb zusaetzlich an die Sichtbarkeit haengen.
    //
    // Zweiter Zweck: HOME muss IMMER auf dem Home-Bildschirm landen. Sitzt der
    // Gast in der gestarteten OTT-App (YouTube & Co.), bekommt unsere App die
    // HOME-Taste gar nicht zu sehen - der TV holt sie nur wieder in den
    // Vordergrund, und sie stuende dann weiter auf der QR-Seite. Sobald wir aus
    // dem Hintergrund zurueckkommen UND vorher eine Fremd-App gestartet wurde,
    // gehen wir deshalb selbst nach Home. visibilitychange feuert nur bei einem
    // echten Wechsel, "sichtbar" heisst also immer "aus dem Hintergrund zurueck".
    //
    // Nicht nur nach einem Start VON HIER (externalAppLaunched): castet der
    // Gast vom Handy, holt der TV YouTube & Co. selbst nach vorne, und die App
    // weiss davon nichts. HOME in YouTube fuehrte dann zurueck auf die
    // Kachelliste statt auf die Hauptseite - bei Netflix ging es nur deshalb,
    // weil wir Netflix selbst gestartet hatten. Deshalb zaehlt jetzt jede
    // Rueckkehr nach mehr als FOREIGN_APP_MIN_MS im Hintergrund.
    onVisibilityChange() {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        this.hiddenAt = Date.now();
        this.cancelAutoForward();
        return;
      }
      this.silenceTuner();
      const away = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
      this.hiddenAt = 0;
      if (this.externalAppLaunched || away >= FOREIGN_APP_MIN_MS) {
        this.externalAppLaunched = false;
        this.goHome();
      }
    },
    goHome() {
      this.$store.dispatch('navigation/to', NAV_TYPE.HOME);
    },
    // Eine Ebene zurueck: Kacheln -> Verbindungsseite -> Home. Aus der
    // Startansicht (Direktstart) direkt nach Home.
    goBack() {
      this.backFocused = false;
      if (this.step === 'tiles') {
        // Der Gast will ausdruecklich zum QR-Code - dort nicht gleich wieder
        // weiterspringen, nur weil das Handy noch als verbunden gilt.
        this.autoForwardDeclined = true;
        this.step = 'pairing';
        this.silenceTuner();
        return;
      }
      this.goHome();
    },
    eventHandler(key) {
      // Steht eine Sicherheitsabfrage, gehoert ihr JEDE Taste - auch HOME und
      // die Farbtasten.
      if (this.dialog) {
        this.dialogKey(key);
        return;
      }
      // Laeuft der Countdown zum Weitergehen, heisst ZURUECK "hier beim
      // QR-Code bleiben" - nicht "Seite verlassen". OK geht sofort weiter
      // (unten, wie immer auf der Verbindungsseite).
      if (this.autoForward > 0 && (key === KEYS.BACK || key === KEYS.GOBACK)) {
        this.cancelAutoForward();
        this.autoForwardDeclined = true;
        return;
      }
      // HOCH fokussiert die "Zurueck"-Schaltflaeche, RUNTER verlaesst sie
      // wieder (zurueck auf Kacheln bzw. QR). ENTER auf "Zurueck" geht eine
      // Ebene hoch. So ist Home in beiden Ansichten per Fernbedienung erreichbar.
      switch (key) {
        case KEYS.HOME:
          // Redundant zum globalen Handler im keyHandler-Mixin, aber billig:
          // faengt Firmwares ab, die HOME erst an die aktive Seite geben.
          this.goHome();
          return;
        case KEYS.UP:
          this.backFocused = true;
          return;
        case KEYS.DOWN:
          this.backFocused = false;
          return;
        case KEYS.BACK:
        case KEYS.GOBACK:
          this.goBack();
          return;
        case KEYS.BLUE:
          // Bildschirmuebertragung / natives Cast-Overlay des TV oeffnen.
          this.openCastEntry();
          return;
        case KEYS.RED:
          // Anmeldedaten des Gastes entfernen - mit Sicherheitsabfrage. Neben
          // BLAU, weil beides Aktionen am Geraet sind und nicht Navigation.
          this.openClearDialog();
          return;
        default:
          break;
      }

      // Startansicht: waehrend des Starts tut OK nichts (ein zweiter Start
      // waere sinnlos), nach einem Fehlschlag geht es zu den uebrigen Apps.
      // ZURUECK fuehrt oben ueber goBack auf die Startseite.
      if (this.step === 'launch') {
        if (key === KEYS.ENTER) {
          if (this.backFocused) this.goBack();
          else if (this.launchState === 'failed') this.leaveLaunch();
        }
        return;
      }

      // Verbindungsseite: ENTER geht weiter zu den Kacheln. Noetig fuer Gaeste,
      // die schon im Netz sind, und fuer Geraete, die Verbindungen nicht melden
      // (LG) - dort schaltet nichts automatisch weiter.
      if (this.step === 'pairing') {
        if (key === KEYS.ENTER) {
          if (this.backFocused) this.goBack();
          else this.showTiles();
        }
        return;
      }

      switch (key) {
        case KEYS.RIGHT:
          if (!this.backFocused) this.$store.dispatch('cast/incSelected');
          break;
        case KEYS.LEFT:
          if (!this.backFocused) this.$store.dispatch('cast/decSelected');
          break;
        case KEYS.ENTER:
          if (this.backFocused) this.goBack();
          else if (this.apps.length) this.chooseSelected();
          break;
        default:
          break;
      }
    },
  },
  watch: {
    selectedIndex() {
      this.keepSelectedVisible();
    },
    apps() {
      this.keepSelectedVisible();
    },
    // Sobald sich ein Geraet am SoftAP meldet, weiter zur Kachelauswahl bzw.
    // in die Direkt-App - nach einem kurzen Countdown, damit der Gast mit
    // ZURUECK beim QR-Code bleiben kann. Nur von der Verbindungsseite aus -
    // verbindet sich spaeter ein zweites Handy, soll die Seite nicht springen.
    // Eine NEUE Verbindung zaehlt auch nach einem abgebrochenen Countdown: der
    // Gast hat dann gerade den QR-Code gescannt.
    'pairing.connected': function watchConnected(connected) {
      if (!connected) {
        this.cancelAutoForward();
        return;
      }
      if (this.step === 'pairing') this.startAutoForward();
    },
  },
  mounted() {
    // Direktstart? Dann steht der App-Schluessel in der Adresse
    // (/cast?app=netflix). Der Rest der Seite laeuft unveraendert: erst die
    // Verbindung, dann diese eine App.
    const query = this.$route && this.$route.query;
    this.directKey = String((query && query.app) || '')
      .trim()
      .toLowerCase();
    // Beim Betreten der Cast-Seite: kein Video, also auch keinen Ton laufen
    // lassen. Betrifft vor allem den Weg TV-Player -> Cast.
    this.silenceTuner();
    if (typeof document !== 'undefined') {
      this._onVisible = () => this.onVisibilityChange();
      document.addEventListener('visibilitychange', this._onVisible);
      // WebOS-Firmwares, die die App suspendieren statt sie nur zu verstecken,
      // melden die Rueckkehr ueber webOSRelaunch statt visibilitychange.
      // Ein Relaunch heisst immer: der TV hat uns aus einer anderen App
      // zurueckgeholt - auch wenn der Gast die App vom Handy aus gestartet hat.
      this._onRelaunch = () => {
        this.externalAppLaunched = false;
        this.goHome();
      };
      document.addEventListener('webOSRelaunch', this._onRelaunch);
    }
    // Erst nach der Geraete-Erkennung pruefen: vorher steht die Strategie nur
    // auf der (evtl. aus dem UserAgent geratenen) Version.
    // Kachelliste vom Cast-Menuepunkt ('#cast?apps=youtube,dazn'), siehe
    // castAppsFromUrl in navigation.js und die Rangfolge in cast/init.
    const menuApps = String((query && query.apps) || '')
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter((k) => !!k);
    this.$store
      .dispatch('cast/init', { apps: menuApps })
      .then(() => {
        // Verbindungsseite gleich aufbauen - sie ist der erste Schritt fuer
        // jeden Weg, also auch fuer Netflix.
        this.preparePairing();
        if (this.$store.getters['cast/isSupported']) return;
        this.$store.dispatch('notification/new', {
          title: this.$i18n('cast.title'),
          message: this.$i18n('cast.unsupported'),
        });
      })
      .catch((e) => {
        // Ohne diesen Zweig blieb die Seite fuer immer auf "wird vorbereitet"
        // stehen, wenn cast/init scheitert.
        console.log('[Cast] init failed', e);
        this.preparing = false;
      });
  },
  beforeDestroy() {
    this.stopHotspotRetry();
    this.cancelAutoForward();
    if (typeof document !== 'undefined') {
      if (this._onVisible) {
        document.removeEventListener('visibilitychange', this._onVisible);
        this._onVisible = null;
      }
      if (this._onRelaunch) {
        document.removeEventListener('webOSRelaunch', this._onRelaunch);
        this._onRelaunch = null;
      }
    }
  },
};
</script>

<style scoped>
.cast {
  background-size: cover;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  padding: 60px 90px;
  position: relative;
  width: 100vw;
}
.cast__header {
  align-items: center;
  color: #f1f1f1;
  display: flex;
  font-size: 3vh;
  justify-content: space-between;
  left: 0;
  padding: 0 2vw;
  position: absolute;
  top: 0;
  height: 8vh;
  width: 100%;
}
.cast__back {
  flex: 0 0 20%;
  text-align: left;
  border: 0.3vh solid transparent;
  border-radius: 0.5vh;
  padding: 0.3vh 0.8vw;
  transition: all 0.2s ease;
}
.cast__back--active {
  border-color: #f1f1f1;
  background-color: rgba(0, 120, 215, 0.85);
  transform: scale(1.05);
}
.cast__back-icon {
  margin-right: 0.5vw;
}
.cast__title {
  flex: 0 0 60%;
  font-weight: bold;
  text-align: center;
}
.cast__spacer {
  flex: 0 0 20%;
}
/* Kachelansicht: Kacheln fuellen die Hoehe, die Hinweise stehen darunter. */
.cast__tiles {
  display: flex;
  flex: 1;
  flex-direction: column;
}
.cast__grid {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
}
/* Sichtfenster der scrollenden Reihe. Oben/unten Luft, damit die
   vergroesserte aktive Kachel (scale) samt Schein nicht abgeschnitten wird. */
.cast__viewport {
  overflow: hidden;
  padding: 3vh 0;
}
/* Abstand ueber margin statt gap: gap fuer Flexbox fehlt auf aelteren
   TV-Browsern, und die Verschiebung rechnet mit genau TILE_PITCH_VW je Kachel. */
.cast__track {
  display: flex;
  transition: transform 0.25s ease;
}
.cast__track--centered {
  justify-content: center;
}
.cast__arrow {
  color: #f1f1f1;
  font-size: 8vh;
  line-height: 1;
  opacity: 0.8;
  padding: 0 1vw;
}
/* Unsichtbar statt entfernt: die Reihe soll beim Erreichen des Endes nicht
   seitlich springen. */
.cast__arrow--hidden {
  visibility: hidden;
}
.cast__tile {
  align-items: center;
  background-color: #ffffff;
  border: 0.4vh solid #cccccc;
  border-radius: 1vh;
  box-sizing: border-box;
  color: #222222;
  display: flex;
  font-size: 3vh;
  flex: 0 0 18vw;
  font-weight: bold;
  height: 26vh;
  justify-content: center;
  margin: 0 1vw;
  padding: 2vh;
  text-align: center;
  transition: transform 0.2s ease, border-width 0.2s ease, box-shadow 0.2s ease;
  width: 18vw;
}
.cast__tile--active {
  border-width: 0.7vh;
  transform: scale(1.06);
  box-shadow: 0 0 2.2vh rgba(255, 255, 255, 0.55);
}
.cast__tile-logo {
  display: block;
  max-height: 60%;
  max-width: 82%;
  object-fit: contain;
}
.cast__tile-name {
  border-radius: 0.5vh;
  padding: 0.5vh 1vh;
  width: 100%;
}
.cast__footer {
  padding-bottom: 2vh;
}
.cast__footer-hint {
  color: #f1f1f1;
  font-size: 2.2vh;
  line-height: 1.4;
  margin-top: 1vh;
  opacity: 0.8;
  text-align: center;
}
.cast__empty {
  color: #f1f1f1;
  font-size: 3vh;
}
.cast__pairing {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
}
/* Zeile aus Info-Kasten + QR-Kasten: gleiche Hoehe durch stretch, die Zeile
   selbst ist nur so hoch wie ihr hoechstes Kind (der QR-Kasten). */
.cast__pairing-row {
  align-items: stretch;
  display: flex;
  gap: 4vw;
}
.cast__pairing-info {
  color: #f1f1f1;
  max-width: 40vw;
  /* Neutralgrauer, leicht transparenter Kasten neben dem QR-Code. */
  background: rgba(60, 60, 60, 0.55);
  border-radius: 1.5vh;
  box-sizing: border-box;
  padding: 3vh 3vw;
  backdrop-filter: blur(8px);
  /* Inhalt vertikal zentriert, waehrend der Kasten auf QR-Hoehe streckt. */
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.cast__pairing-app {
  font-size: 5vh;
  font-weight: bold;
  margin-bottom: 2vh;
}
.cast__pairing-text {
  font-size: 3vh;
  line-height: 1.4;
  margin-bottom: 3vh;
}
.cast__pairing-access {
  /* Zwei Zeilen, mit Luft dazwischen - sie werden abgetippt. */
  line-height: 1.6;
}
.cast__pairing-ssid {
  font-size: 2.5vh;
}
.cast__pairing-status {
  align-items: center;
  display: flex;
  font-size: 2.5vh;
  margin-top: 2vh;
}
.cast__pairing-dot {
  background-color: #ffc107;
  border-radius: 50%;
  display: inline-block;
  height: 1.4vh;
  margin-right: 1vh;
  width: 1.4vh;
  /* Pulsiert, solange gewartet wird - im Ruhezustand ist nicht erkennbar,
     ob die Seite ueberhaupt noch etwas tut. */
  animation: cast-pulse 1.4s ease-in-out infinite;
}
.cast__pairing-status--ok {
  color: #7ddf8f;
  font-weight: bold;
}
.cast__pairing-status--ok .cast__pairing-dot {
  animation: none;
  background-color: #7ddf8f;
}
@keyframes cast-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.25;
  }
}
.cast__pairing-hint {
  font-size: 2.2vh;
  margin-top: 2vh;
  opacity: 0.8;
}
.cast__launch-detail {
  font-size: 1.8vh;
  margin-top: 2vh;
  opacity: 0.45;
}
.cast__qr {
  align-items: center;
  background: #ffffff;
  border-radius: 1vh;
  display: flex;
  justify-content: center;
  padding: 2vh;
}
.cast__qr >>> svg {
  height: 40vh;
  width: 40vh;
}
/* Platzhalter waehrend der Vorbereitung: gleiche Aussenmasse wie der fertige
   QR (40vh + 2x2vh Innenabstand), damit die Seite beim Erscheinen nicht
   springt. Gedeckt statt weiss, damit er nicht wie ein kaputter Code wirkt. */
.cast__qr--wait {
  background: rgba(255, 255, 255, 0.12);
  height: 40vh;
  width: 40vh;
}
/* Zahl der Einschaltversuche. Klein und gedeckt - sie ist fuer das Personal
   gedacht, nicht fuer den Gast, aber sie gehoert dorthin, wo man hinsieht. */
.cast__qr-tries {
  color: rgba(255, 255, 255, 0.5);
  font-size: 2vh;
  margin-left: 1vw;
}
/* Sicherheitsabfrage. Deckend abgedunkelt, damit der Gast sie nicht mit dem
   QR-Bereich verwechselt, und ueber allem - auch ueber dem Kopfbereich. */
.cast__dialog {
  align-items: center;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 10;
}
.cast__dialog-box {
  background: rgba(40, 40, 40, 0.96);
  border-radius: 1.5vh;
  box-sizing: border-box;
  color: #f1f1f1;
  max-width: 60vw;
  padding: 5vh 5vw;
  text-align: center;
}
.cast__dialog-title {
  font-size: 4.5vh;
  font-weight: bold;
  margin-bottom: 2.5vh;
}
.cast__dialog-text {
  font-size: 3vh;
  line-height: 1.4;
}
.cast__dialog-buttons {
  display: flex;
  gap: 2vw;
  justify-content: center;
  margin-top: 4vh;
}
.cast__dialog-button {
  border: 0.3vh solid rgba(241, 241, 241, 0.35);
  border-radius: 0.8vh;
  font-size: 3vh;
  padding: 1.2vh 2.5vw;
  transition: all 0.2s ease;
}
.cast__dialog-button--active {
  background-color: rgba(0, 120, 215, 0.85);
  border-color: #f1f1f1;
  transform: scale(1.05);
}
.cast__qr-dot {
  background-color: #ffc107;
  border-radius: 50%;
  display: inline-block;
  height: 2vh;
  width: 2vh;
  animation: cast-pulse 1.4s ease-in-out infinite;
}
</style>



// WEBPACK FOOTER //
// src/pages/Cast.vue